import Config from 'config';
import {
  differenceInDays,
  differenceInBusinessDays,
  getYear,
  isAfter,
  isBefore,
  endOfDay,
  startOfDay,
  endOfToday,
  startOfTomorrow
} from 'date-fns';
import { FormikValues } from 'formik';
import { TFunction } from 'i18next';
import { AcademicSession } from 'types/schema-generated-types';
import { EndOfYearProcessingFormValues, EndOfYearProcessingNotificationType } from '../types';

const {
  endOfYear: { notificationDays, minAdvanceDays, limitDay }
} = Config;

export const getActiveAcademicSession = (
  academicSessions: AcademicSession[]
): AcademicSession | undefined => academicSessions.find(a => !a.default && a.active);

export const getNotificationType = (
  academicSessions: AcademicSession[]
): EndOfYearProcessingNotificationType | undefined => {
  if (academicSessions.length === 0) return undefined;

  const today = endOfDay(new Date());
  const activeAcademicSession = getActiveAcademicSession(academicSessions);
  if (!activeAcademicSession) return undefined;

  if (differenceInDays(new Date(activeAcademicSession.dateTermEnd), today) <= notificationDays) {
    return EndOfYearProcessingNotificationType.UPCOMING;
  }

  return undefined;
};

export const validateForm = (
  formValues: EndOfYearProcessingFormValues,
  initialValues: EndOfYearProcessingFormValues,
  t: TFunction
): FormikValues => {
  const errors: FormikValues = {};
  const today = endOfToday();
  const maxDate = endOfDay(new Date(`${limitDay} ${getYear(new Date())}`));
  const dateTermEnd = formValues.dateTermEnd && endOfDay(new Date(formValues.dateTermEnd));
  const upcomingDateTermStart =
    formValues.upcomingDateTermStart && startOfDay(new Date(formValues.upcomingDateTermStart));

  if (initialValues.dateTermEnd && !dateTermEnd) {
    errors.dateTermEnd = t('End of Year Date is required');
  }

  if (dateTermEnd && (isBefore(dateTermEnd, startOfTomorrow()) || isAfter(dateTermEnd, maxDate))) {
    errors.dateTermEnd = t(
      `Date has to be at least tomorrow and not after ${limitDay} of the current year`
    );
  }

  if (
    upcomingDateTermStart &&
    (differenceInBusinessDays(upcomingDateTermStart, dateTermEnd || today) < minAdvanceDays ||
      isAfter(upcomingDateTermStart, maxDate))
  ) {
    errors.newYearDateTermStart = t(
      `Date has to be at least ${minAdvanceDays} business days from ${
        dateTermEnd ? 'the chosen end date' : 'today'
      } and not after ${limitDay} of the current year`
    );
  }

  return errors;
};
